import React, { useState } from "react";
import { Phone, Mail, Send, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function ContactPage() {
  useSeo("Contact OyeBest", "Get in touch with the OyeBest team. Phone, email and contact form.");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ name: "", mobile: "", email: "", subject: "", message: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.mobile || !f.message) { toast.error("Name, mobile and message are required"); return; }
    setLoading(true);
    try { await api.post("/contact", f); setDone(true); }
    catch (e) { toast.error("Could not send. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Contact OyeBest</h1>
      <p className="text-muted-foreground mt-2">Koi sawaal ya suggestion? Hum yahan hain aapki madad ke liye.</p>

      <div className="grid md:grid-cols-2 gap-8 mt-8">
        <div className="space-y-4">
          <a href="tel:8920255908" className="flex items-center gap-3 rounded-2xl border border-border p-5 bg-white hover:border-primary/40 transition-colors" data-testid="contact-phone">
            <span className="w-11 h-11 rounded-xl bg-accent text-primary flex items-center justify-center"><Phone className="w-5 h-5" /></span>
            <div><p className="text-sm text-muted-foreground">Phone</p><p className="font-bold">8920255908</p></div>
          </a>
          <a href="mailto:oyebest.in@gmail.com" className="flex items-center gap-3 rounded-2xl border border-border p-5 bg-white hover:border-primary/40 transition-colors" data-testid="contact-email-1">
            <span className="w-11 h-11 rounded-xl bg-accent text-primary flex items-center justify-center"><Mail className="w-5 h-5" /></span>
            <div><p className="text-sm text-muted-foreground">Primary Email</p><p className="font-bold">oyebest.in@gmail.com</p></div>
          </a>
          <a href="mailto:ompgroup.of@gmail.com" className="flex items-center gap-3 rounded-2xl border border-border p-5 bg-white hover:border-primary/40 transition-colors" data-testid="contact-email-2">
            <span className="w-11 h-11 rounded-xl bg-accent text-primary flex items-center justify-center"><Mail className="w-5 h-5" /></span>
            <div><p className="text-sm text-muted-foreground">Secondary Email</p><p className="font-bold">ompgroup.of@gmail.com</p></div>
          </a>
        </div>

        <div className="rounded-2xl border border-border p-6 bg-white">
          {done ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-7 h-7" /></div>
              <p className="font-semibold">Message bhej diya gaya!</p>
              <p className="text-sm text-muted-foreground mt-1">We'll get back to you soon.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <Field label="Name *"><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="rounded-xl h-12" data-testid="cp-name" /></Field>
              <Field label="Mobile *"><Input value={f.mobile} onChange={(e) => set("mobile", e.target.value)} className="rounded-xl h-12" data-testid="cp-mobile" /></Field>
              <Field label="Email"><Input value={f.email} onChange={(e) => set("email", e.target.value)} className="rounded-xl h-12" data-testid="cp-email" /></Field>
              <Field label="Subject"><Input value={f.subject} onChange={(e) => set("subject", e.target.value)} className="rounded-xl h-12" data-testid="cp-subject" /></Field>
              <Field label="Message *"><Textarea value={f.message} onChange={(e) => set("message", e.target.value)} className="rounded-xl" rows={4} data-testid="cp-message" /></Field>
              <Button type="submit" disabled={loading} className="w-full rounded-full h-12 font-semibold gap-2" data-testid="cp-submit"><Send className="w-4 h-4" /> {loading ? "Sending…" : "Send Message"}</Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-sm font-semibold">{label}</Label>{children}</div>;
}
